import { useMemo } from 'react'
import { usePortfolioAnalyticsBook } from '@/hooks/useDemoDataAccess'
import { chartAlertColor, chartSeriesColor } from '@/config/chartTheme'

const fmt = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(2)}B` : `$${v.toFixed(1)}M`
const pct = (v: number, d = 1) => `${(v * 100).toFixed(d)}%`

import type { Loan } from '@/types'

interface MSAExposure {
  msa: string
  deals: number
  volume: number
  pctOfAum: number
  avgVacRate: number | null
  avgRentGrowth: number | null
  avgCapRate: number | null
  avgAbsorption: number | null
  riskScore: number
  limitBreach: boolean
}

const EXPOSURE_LIMIT = 0.25 // 25% max per MSA

function riskColor(score: number): string {
  if (score <= 3) return 'text-emerald-400'
  if (score <= 6) return 'text-amber-400'
  return 'text-red-400'
}

function heatCell(value: number, isGood: boolean): string {
  // Higher = better for some metrics, worse for others
  const intensity = Math.min(Math.abs(value) / 10, 1)
  if (isGood) {
    return intensity > 0.5 ? 'bg-emerald-400/20 text-emerald-400' : 'bg-emerald-400/10 text-emerald-300'
  }
  return intensity > 0.5 ? 'bg-red-400/20 text-red-400' : 'bg-red-400/10 text-red-300'
}

/**
 * "City, ST" from a location when the loan records no MSA. A live loan's
 * location is a full street address ("12 Harbor Rd, Portland, ME 04101"), and
 * taking its last comma part grouped the book by "ME 04101".
 */
function marketOfLocation(location: string | null | undefined): string {
  const parts = (location ?? '').split(',').map((p) => p.trim()).filter(Boolean)
  if (parts.length === 0) return 'Other'
  const state = parts[parts.length - 1].replace(/\s*\d{5}(-\d{4})?$/, '')
  const city = parts.length >= 2 ? parts[parts.length - 2] : ''
  return city ? `${city}, ${state}` : state || 'Other'
}

export default function MarketExposureAnalysis() {
  const deals = usePortfolioAnalyticsBook().complete
  const totalAum = deals.reduce((s, d) => s + d.loanAmount, 0)

  const msaData = useMemo(() => {
    const msaMap = new Map<string, { deals: Loan[]; volume: number }>()
    deals.forEach((d) => {
      const msa = d.market?.msa || marketOfLocation(d.location)
      const existing = msaMap.get(msa) ?? { deals: [], volume: 0 }
      existing.deals.push(d)
      existing.volume += d.loanAmount
      msaMap.set(msa, existing)
    })

    const exposures: MSAExposure[] = [...msaMap.entries()]
      .sort((a, b) => b[1].volume - a[1].volume)
      .map(([msa, data]) => {
        // Averages over the loans that RECORD the figure. A live loan with no
        // market data used to contribute 0 (or NaN), which read as a real
        // 0.0% vacancy for the whole market.
        const avgOf = (pick: (d: Loan) => number | undefined) => {
          const vals = data.deals.map(pick).filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
          return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null
        }
        const avgVac = avgOf((d) => d.market?.vacRate)
        const avgRent = avgOf((d) => d.market?.rentGrowth)
        // Cap rate is stored as a fraction by intake and a percent by the
        // curated seed; normalise each loan before averaging.
        const avgCap = avgOf((d) => {
          const c = d.market?.capRate
          return typeof c === 'number' && c > 0 ? (c < 1 ? c * 100 : c) : undefined
        })
        const avgAbs = avgOf((d) => d.market?.absorption)
        const pctOfAum = data.volume / totalAum

        // Simple risk scoring: higher vacancy, lower rent growth, higher cap rate = worse
        const vacScore = avgVac == null ? 1 : avgVac > 8 ? 3 : avgVac > 5 ? 2 : 1
        const rentScore = avgRent == null ? 1 : avgRent < 1 ? 3 : avgRent < 3 ? 2 : 1
        const concScore = pctOfAum > 0.25 ? 3 : pctOfAum > 0.15 ? 2 : 1
        const riskScore = vacScore + rentScore + concScore

        return {
          msa,
          deals: data.deals.length,
          volume: data.volume,
          pctOfAum,
          avgVacRate: avgVac,
          avgRentGrowth: avgRent,
          avgCapRate: avgCap,
          avgAbsorption: avgAbs,
          riskScore,
          limitBreach: pctOfAum > EXPOSURE_LIMIT,
        }
      })

    return exposures
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const breachCount = msaData.filter((m) => m.limitBreach).length
  const topMsa = msaData[0]

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        <div className="metric-cell">
          <div className="metric-cell-label">Markets</div>
          <div className="metric-cell-value">{msaData.length}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Largest Concentration</div>
          <div className="metric-cell-value">{topMsa?.msa} ({pct(topMsa?.pctOfAum ?? 0)})</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Exposure Limit ({pct(EXPOSURE_LIMIT, 0)})</div>
          <div className={`metric-cell-value ${breachCount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
            {breachCount} breach{breachCount !== 1 ? 'es' : ''}
          </div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Portfolio AUM</div>
          <div className="metric-cell-value">{fmt(totalAum)}</div>
        </div>
      </div>

      {/* Market Heatmap */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Market Exposure Heatmap</span>
          <span className="text-[10px] text-stone-600 font-mono">Max {pct(EXPOSURE_LIMIT, 0)} per MSA</span>
        </div>
        <div className="overflow-x-auto">
          <table className="dense-table">
            <thead>
              <tr>
                <th>MSA / Market</th>
                <th className="text-right">Deals</th>
                <th className="text-right">Volume</th>
                <th className="text-right">% of AUM</th>
                <th className="text-center">Vacancy</th>
                <th className="text-center">Rent Growth</th>
                <th className="text-center">Cap Rate</th>
                <th className="text-center">Absorption</th>
                <th className="text-center">Risk Score</th>
                <th className="text-center">Limit</th>
              </tr>
            </thead>
            <tbody>
              {msaData.map((m) => (
                <tr key={m.msa} className={m.limitBreach ? 'bg-red-400/[0.03]' : ''}>
                  <td className="font-medium text-stone-200 whitespace-nowrap">{m.msa}</td>
                  <td className="text-right font-mono">{m.deals}</td>
                  <td className="text-right font-mono">{fmt(m.volume)}</td>
                  <td className={`text-right font-mono ${m.pctOfAum > 0.20 ? 'text-amber-400' : 'text-stone-200'}`}>
                    {pct(m.pctOfAum)}
                  </td>
                  <td className={`text-center font-mono rounded-sm px-1 ${m.avgVacRate == null ? 'text-stone-500' : heatCell(m.avgVacRate, m.avgVacRate < 5)}`}>
                    {m.avgVacRate == null ? '—' : `${m.avgVacRate.toFixed(1)}%`}
                  </td>
                  <td className={`text-center font-mono rounded-sm px-1 ${m.avgRentGrowth == null ? 'text-stone-500' : heatCell(m.avgRentGrowth, m.avgRentGrowth > 2)}`}>
                    {m.avgRentGrowth == null ? '—' : `${m.avgRentGrowth.toFixed(1)}%`}
                  </td>
                  <td className="text-center font-mono text-stone-300">{m.avgCapRate == null ? '—' : `${m.avgCapRate.toFixed(1)}%`}</td>
                  <td className={`text-center font-mono rounded-sm px-1 ${m.avgAbsorption == null ? 'text-stone-500' : heatCell(m.avgAbsorption, m.avgAbsorption > 0)}`}>
                    {m.avgAbsorption == null ? '—' : `${m.avgAbsorption > 0 ? '+' : ''}${m.avgAbsorption.toFixed(0)}K SF`}
                  </td>
                  <td className="text-center">
                    <span className={`font-mono font-bold ${riskColor(m.riskScore)}`}>{m.riskScore}/9</span>
                  </td>
                  <td className="text-center">
                    {m.limitBreach ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-red-400" title="Exceeds limit" />
                    ) : (
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" title="Within limit" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Concentration Bar */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Geographic Concentration</span>
        </div>
        <div className="panel-body">
          <div className="h-8 w-full rounded-md overflow-hidden flex border border-amber-400/10">
            {msaData.map((m, i) => (
              <div
                key={m.msa}
                className={`chart-viz-bar flex items-center justify-center text-[9px] font-medium ${
                  m.limitBreach ? 'text-white' : i % 2 === 0 ? 'text-stone-900' : 'text-stone-100'
                }`}
                style={{
                  width: `${m.pctOfAum * 100}%`,
                  minWidth: m.pctOfAum > 0.08 ? undefined : 0,
                  background: m.limitBreach ? chartAlertColor() : chartSeriesColor(i % 6),
                }}
                title={`${m.msa}: ${pct(m.pctOfAum)}`}
              >
                {m.pctOfAum > 0.08 && `${m.msa} ${(m.pctOfAum * 100).toFixed(0)}%`}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-2 text-[9px] text-stone-600">
            <span>Exposure limit: {pct(EXPOSURE_LIMIT, 0)} per MSA</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: chartAlertColor() }} /> Over limit</span>
          </div>
        </div>
      </div>
    </div>
  )
}
